import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Mail, Linkedin, Github, MapPin, ArrowUpRight } from 'lucide-react';
import { useTheme } from '../ThemeContext';
import SectionHeader from './SectionHeader';

const contactLinks = [
  { icon: Mail, label: 'Email', value: 'sabu6516@colorado.edu', href: 'mailto:sabu6516@colorado.edu' },
  { icon: Linkedin, label: 'LinkedIn', value: '/in/samuel-buckler', href: 'https://linkedin.com/in/samuel-buckler' },
  { icon: Github, label: 'GitHub', value: 'github.com/sabu6516', href: 'https://github.com/sabu6516' },
  { icon: MapPin, label: 'Location', value: 'Boulder, Colorado', href: null },
];

export default function Contact() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const { isCyberpunk } = useTheme();

  return (
    <section id="contact" className={`relative py-24 px-6 transition-colors duration-700 ${
      isCyberpunk ? 'cyber-grid bg-cyber-black' : 'bg-gray-50'
    }`}>
      <div className="max-w-4xl mx-auto">
        <SectionHeader number="05." title="CONTACT" />

        <div ref={ref} className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >

            <h3 className={`font-display text-4xl sm:text-5xl font-bold mb-6 tracking-wider transition-colors duration-500 ${
              isCyberpunk ? 'text-white' : 'text-gray-900'
            }`}>
              GET IN{' '}
              <span className={isCyberpunk ? 'text-cyber-magenta neon-text-subtle' : 'text-indigo-600'}>
                TOUCH
              </span>
            </h3>

            <p className={`font-body text-lg max-w-xl mx-auto leading-relaxed mb-10 transition-colors duration-500 ${
              isCyberpunk ? 'text-cyber-dim/70' : 'text-gray-500'
            }`}>
              I'm currently looking for new opportunities in machine learning research
              and software engineering. Whether you have a question or just want to connect,
              my inbox is always open.
            </p>

            <motion.a
              href="mailto:sabu6516@colorado.edu"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              className={`inline-flex items-center gap-2 font-mono text-sm px-8 py-4 tracking-widest border-2 transition-all duration-500 ${
                isCyberpunk
                  ? 'border-cyber-magenta/60 text-cyber-magenta hover:bg-cyber-magenta/10 hover:shadow-[0_0_30px_rgba(255,0,255,0.2)]'
                  : 'border-indigo-600 text-indigo-600 hover:bg-indigo-600 hover:text-white rounded-lg'
              }`}
            >
              SEND MESSAGE
              <ArrowUpRight size={16} />
            </motion.a>
          </motion.div>
        </div>

        {/* Contact cards */}
        <div className="grid sm:grid-cols-2 gap-4">
          {contactLinks.map(({ icon: Icon, label, value, href }, i) => {
            const inner = (
              <>
                <div className={`w-10 h-10 flex items-center justify-center border transition-colors duration-500 ${
                  isCyberpunk
                    ? 'border-cyber-magenta/20 text-cyber-magenta group-hover:bg-cyber-magenta/10'
                    : 'border-indigo-200 text-indigo-500 rounded-lg group-hover:bg-indigo-50'
                }`}>
                  <Icon size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`font-mono text-xs uppercase tracking-wider transition-colors duration-500 ${
                    isCyberpunk ? 'text-cyber-dim/50' : 'text-gray-400'
                  }`}>{label}</p>
                  <p className={`font-body text-sm truncate transition-colors duration-500 ${
                    isCyberpunk ? 'text-white group-hover:text-cyber-magenta' : 'text-gray-800 group-hover:text-indigo-600'
                  }`}>{value}</p>
                </div>
                {href && (
                  <ArrowUpRight size={14} className={`transition-colors ${
                    isCyberpunk
                      ? 'text-cyber-dim/30 group-hover:text-cyber-magenta'
                      : 'text-gray-300 group-hover:text-indigo-600'
                  }`} />
                )}
              </>
            );

            return (
              <motion.div
                key={label}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.4 + i * 0.1 }}
              >
                {href ? (
                  <a
                    href={href}
                    target={href.startsWith('mailto') ? undefined : '_blank'}
                    rel="noopener noreferrer"
                    className={`p-5 flex items-center gap-4 group block transition-all duration-500 ${
                      isCyberpunk
                        ? 'neon-border bg-cyber-dark/50 hover:bg-cyber-dark/80'
                        : 'bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md'
                    }`}
                  >
                    {inner}
                  </a>
                ) : (
                  <div className={`p-5 flex items-center gap-4 group transition-all duration-500 ${
                    isCyberpunk
                      ? 'neon-border bg-cyber-dark/50'
                      : 'bg-white border border-gray-200 rounded-xl shadow-sm'
                  }`}>
                    {inner}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
